import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import type { CardDto } from '@sal/core/application/dtos';
import type { Card } from '../interfaces/ICardItem';
import { FileService } from './file-service';
import { environment } from '../../../environments/environment';

@Injectable({
    providedIn: 'root'
})
export class TileService {
    private readonly http = inject(HttpClient);
    private readonly translateService = inject(TranslateService);
    private readonly fileService = inject(FileService);
    private readonly apiUrl = environment.endpoint;

    async getCardsByType(type: 'reports' | 'data', language: string): Promise<Card[]> {
        const cards = await firstValueFrom(this.http.get<CardDto[]>(`${this.apiUrl}/content/${type}`));
        return cards.map(dto => ({ ...dto, type, language }));
    }

    public readDocument(key: string): Promise<string | false> {
        return this.fileService.getDocumentSignedUrl(key);
    }

    // CMS Card CRUD Operations
    async saveTile(card: Card): Promise<Card> {
        const body: Record<string, unknown> = {
            id: card.id,
            title: card.title,
            content: card.content,
            tileMode: card.canDownload ? ('document' as const) : ('info' as const),
            category: card.type,
            language: card.language || this.translateService.getCurrentLang() || 'en'
        };
        if (card.canDownload && card.key) {
            body['key'] = card.key;
        }
        const result = await firstValueFrom(this.http.put<CardDto>(`${this.apiUrl}/content`, body));
        return { ...result, type: card.type, language: body.language as string };
    }

    async createTile(card: Partial<Card>): Promise<Card> {
        if (!card.title || !card.type) {
            throw new Error('Card title and type are required');
        }
        const body: Record<string, unknown> = {
            title: card.title,
            content: card.content || '',
            tileMode: card.canDownload ? ('document' as const) : ('info' as const),
            category: card.type,
            language: card.language || this.translateService.getCurrentLang() || 'en'
        };
        if (card.canDownload && card.key) {
            body['key'] = card.key;
        }
        const result = await firstValueFrom(this.http.post<CardDto>(`${this.apiUrl}/content`, body));
        return { ...result, type: card.type, language: body.language as string };
    }

    async deleteTile(card: Card): Promise<void> {
        if (!card.id) {
            throw new Error('Cannot delete card without id');
        }
        return await firstValueFrom(this.http.delete<void>(`${this.apiUrl}/content/${card.id}`));
    }
}
